/**
 * BalanceCells — per-chain projectors for the `/networks/compare`
 * Phase 3 "live USD totals" view.
 *
 * Each chain's data hook (`useAlgorandWallets`, `useCardanoWallets`,
 * etc.) is mounted inside a tiny dedicated component, so the network
 * cost is paid only when the user opts into the Phase 3 view (the
 * parent only renders these cells when `loadBalances === true`).
 *
 * Shape uniformity: each cell projects its chain-specific data to
 * `{ totalUsd, isLoading, hasError }` and delegates the visual to
 * a single shared `<BalanceCellShell>` component.
 *
 * Hooks-of-hooks rule: each Cell can only be mounted once per chain
 * — the dispatcher map below relies on this. Don't add conditional
 * hook calls inside a single Cell.
 *
 * Phase 4 update: TheGraph aggregation logic was extracted into
 * `useTheGraphAggregated()`, so the 9th cell is now live too.
 */

import { useMemo, type ReactElement } from 'react';
import { useAlgorandWallets } from '../../hooks/useAlgorandWallet';
import { useCardanoWallets } from '../../hooks/useCardanoWallet';
import { useHederaWallets } from '../../hooks/useHederaWallet';
import { useIcpData } from '../../hooks/useIcpWallet';
import { useVechainWallets } from '../../hooks/useVechainWallet';
import { useXdcWallets } from '../../hooks/useXdcWallet';
import { useAxelarMonitor } from '../../hooks/useAxelarWallet';
import { useCronosStaking } from '../../hooks/useCronosStaking';
import { useTheGraphAggregated } from '../../hooks/useTheGraphAggregated';
import { isValidNeuronId, isValidIcpAccountId } from '../../services/icpApi';
import type { NetworkOverviewKey } from '../../hooks/useNetworksOverview';

// ---- Shared visual shell ---------------------------------------------

interface BalanceCellShellProps {
  totalUsd: number;
  isLoading: boolean;
  hasError?: boolean;
  /** True for chains we deliberately don't query in Phase 3
   *  (TheGraph today). */
  unsupported?: boolean;
}

function BalanceCellShell({
  totalUsd,
  isLoading,
  hasError = false,
  unsupported = false,
}: BalanceCellShellProps) {
  if (unsupported) {
    return (
      <span style={{ color: 'var(--muted)', fontSize: '12px' }} title="Live data not available — open the chain monitor for full details">
        —
      </span>
    );
  }
  if (isLoading) {
    return (
      <span style={{ color: 'var(--muted)', fontSize: '12px' }} aria-live="polite">
        loading…
      </span>
    );
  }
  if (hasError) {
    return (
      <span style={{ color: '#F87171', fontSize: '12px' }} title="Failed to load live balance">
        error
      </span>
    );
  }
  if (totalUsd === 0) {
    return <span style={{ color: 'var(--muted)' }}>$0</span>;
  }
  return (
    <span style={{ fontWeight: 600, color: 'var(--text)' }}>
      ${totalUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
    </span>
  );
}

// ---- Per-chain projectors --------------------------------------------

interface ChainCellProps {
  wallets: string[];
}

function AlgorandBalanceCell({ wallets }: ChainCellProps) {
  const { totalBalanceUsd, isLoading, error } = useAlgorandWallets(wallets);
  return <BalanceCellShell totalUsd={totalBalanceUsd} isLoading={isLoading} hasError={!!error} />;
}

function CardanoBalanceCell({ wallets }: ChainCellProps) {
  const { totalBalanceUsd, isLoading, error } = useCardanoWallets(wallets);
  return <BalanceCellShell totalUsd={totalBalanceUsd} isLoading={isLoading} hasError={!!error} />;
}

function HederaBalanceCell({ wallets }: ChainCellProps) {
  const { totalBalanceUsd, isLoading, error } = useHederaWallets(wallets);
  return <BalanceCellShell totalUsd={totalBalanceUsd} isLoading={isLoading} hasError={!!error} />;
}

function IcpBalanceCell({ wallets }: ChainCellProps) {
  // ICP wallet list mixes neuron IDs and account IDs in the same
  // localStorage bucket — split by validator before calling the
  // data hook. Same logic as in IcpMonitor.tsx.
  const { neuronIds, accountIds } = useMemo(() => ({
    neuronIds: wallets.filter(isValidNeuronId),
    accountIds: wallets.filter(isValidIcpAccountId),
  }), [wallets]);
  const { totalBalanceUsd, totalStakedUsd, isLoading, error } = useIcpData(accountIds, neuronIds);
  // ICP "total value" = liquid balance + staked-in-neurons value
  const totalUsd = totalBalanceUsd + totalStakedUsd;
  return <BalanceCellShell totalUsd={totalUsd} isLoading={isLoading} hasError={!!error} />;
}

function VechainBalanceCell({ wallets }: ChainCellProps) {
  const { totalBalanceUsd, isLoading, error } = useVechainWallets(wallets);
  return <BalanceCellShell totalUsd={totalBalanceUsd} isLoading={isLoading} hasError={!!error} />;
}

function XdcBalanceCell({ wallets }: ChainCellProps) {
  const { totalBalanceUsd, isLoading, error } = useXdcWallets(wallets);
  return <BalanceCellShell totalUsd={totalBalanceUsd} isLoading={isLoading} hasError={!!error} />;
}

function AxelarBalanceCell(_props: ChainCellProps) {
  // useAxelarMonitor() reads its wallets from useAxelarMonitorWallets()
  // internally — no need to pass them in.
  const { totals, isLoading } = useAxelarMonitor();
  return <BalanceCellShell totalUsd={totals.totalUSD} isLoading={isLoading} />;
}

function CronosBalanceCell({ wallets }: ChainCellProps) {
  const { aggregatedData, isLoading, error } = useCronosStaking(wallets);
  return (
    <BalanceCellShell
      totalUsd={aggregatedData?.totalValueUsd ?? 0}
      isLoading={isLoading}
      hasError={!!error}
    />
  );
}

function TheGraphBalanceCell({ wallets }: ChainCellProps) {
  const { totalValueUsd, isLoading, error } = useTheGraphAggregated(wallets);
  return <BalanceCellShell totalUsd={totalValueUsd} isLoading={isLoading} hasError={!!error} />;
}

// ---- Dispatcher ------------------------------------------------------

const COMPONENT_BY_CHAIN: Record<NetworkOverviewKey, (p: ChainCellProps) => ReactElement> = {
  'algorand-monitor': AlgorandBalanceCell,
  'axelar-monitor': AxelarBalanceCell,
  'cardano-monitor': CardanoBalanceCell,
  'cronos-monitor': CronosBalanceCell,
  'hedera-monitor': HederaBalanceCell,
  'icp-monitor': IcpBalanceCell,
  'thegraph-monitor': TheGraphBalanceCell,
  'vechain-monitor': VechainBalanceCell,
  'xdc-monitor': XdcBalanceCell,
};

interface BalanceCellProps {
  pageKey: NetworkOverviewKey;
  wallets: string[];
}

export function BalanceCell({ pageKey, wallets }: BalanceCellProps) {
  const Component = COMPONENT_BY_CHAIN[pageKey];
  if (!Component) {
    return <BalanceCellShell totalUsd={0} isLoading={false} unsupported />;
  }
  if (wallets.length === 0) {
    return <span style={{ color: 'var(--muted)' }}>—</span>;
  }
  return <Component wallets={wallets} />;
}
